import { useState, useRef, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { supabase } from '@/lib/supabase';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function ContactPage() {
  const { t } = useTranslation();
  const { wechat_qr_url, whatsapp_qr_url, contact } = useSiteSettings();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qrType, setQrType] = useState<'wechat' | 'whatsapp' | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapImgError, setMapImgError] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const contactJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: '联系我们 - RTB吉尔吉斯斯坦试管代孕中心',
    description: '联系RTB吉尔吉斯斯坦试管代孕中心，在线预约免费咨询，微信/WhatsApp/电话均可联系，24小时响应。',
    url: `${SITE_URL}/contact`,
    mainEntity: {
      '@type': 'LocalBusiness',
      name: 'RTB吉尔吉斯斯坦试管代孕中心',
      telephone: contact.phone,
      email: contact.email,
      address: {
        '@type': 'PostalAddress',
        addressLocality: contact.address,
        addressCountry: 'KG',
      },
    },
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;
    const msgField = formRef.current.querySelector<HTMLTextAreaElement>('[name="message"]');
    if (msgField && msgField.value.length > 500) {
      return;
    }
    setLoading(true);
    const fd = new FormData(formRef.current);
    try {
      await supabase.from('customers').insert({
        name: fd.get('name') as string || '',
        phone: fd.get('phone') as string || '',
        wechat: fd.get('wechat') as string || '',
        email: fd.get('email') as string || '',
        service: fd.get('service') as string || '',
        message: fd.get('message') as string || '',
        status: 'new',
        source: 'website_contact_form',
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SeoHead
        title="联系我们｜预约免费咨询 - RTB吉尔吉斯斯坦试管代孕中心"
        description="联系RTB吉尔吉斯斯坦试管代孕中心，在线预约免费咨询，微信/WhatsApp/电话均可，24小时专业顾问响应，全程中文一对一服务。"
        keywords="联系代孕中心,预约试管婴儿咨询,吉尔吉斯斯坦代孕咨询,RTB联系方式"
        canonical="/contact"
        jsonLd={contactJsonLd}
      />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="bg-[#1a2f4a] py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('contact_label')}</div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">{t('contact_title')}</h1>
            <p className="text-white/60 text-base">{t('contact_subtitle')}</p>
          </div>
        </section>

        {/* Contact info + Form */}
        <section className="py-14 md:py-20 bg-[#f8f5f0]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Left: Contact info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Quick contact */}
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="font-bold text-[#1a3a5c] text-base mb-5">{t('contact_info_title')}</h3>
                  <div className="space-y-4">
                    <a href={`tel:${contact.phone}`} className="flex items-center gap-4 group cursor-pointer">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1a3a5c]/8 text-[#c9a96e] shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-0.5">{t('contact_phone')}</div>
                        <div className="text-sm font-medium text-[#1a3a5c] group-hover:text-[#c9a96e] transition-colors">{contact.phone}</div>
                      </div>
                    </a>
                    <div
                      onClick={() => setQrType('wechat')}
                      className="flex items-center gap-4 cursor-pointer group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#07c160]/10 text-[#07c160] shrink-0">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-0.5">{t('contact_wechat')}</div>
                        <div className="text-sm font-medium text-[#1a3a5c] group-hover:text-[#07c160] transition-colors">{contact.wechat} <span className="text-xs text-gray-400">{t('contact_wechat_click')}</span></div>
                      </div>
                    </div>
                    <div
                      onClick={() => setQrType('whatsapp')}
                      className="flex items-center gap-4 cursor-pointer group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#25d366]/10 text-[#25d366] shrink-0">
                        <i className="ri-whatsapp-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-0.5">WhatsApp</div>
                        <div className="text-sm font-medium text-[#1a3a5c] group-hover:text-[#25d366] transition-colors">{contact.whatsapp} <span className="text-xs text-gray-400">{t('contact_wechat_click')}</span></div>
                      </div>
                    </div>
                    <a href={`mailto:${contact.email}`} className="flex items-center gap-4 group cursor-pointer">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#c9a96e]/10 text-[#c9a96e] shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-0.5">{t('contact_email')}</div>
                        <div className="text-sm font-medium text-[#1a3a5c] group-hover:text-[#c9a96e] transition-colors">{contact.email}</div>
                      </div>
                    </a>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#1a3a5c]/8 text-[#c9a96e] shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-0.5">{t('contact_address')}</div>
                        <div className="text-sm font-medium text-[#1a3a5c]">{contact.address}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR Codes */}
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="font-bold text-[#1a3a5c] text-base mb-4">{t('contact_qr_title')}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center cursor-pointer" onClick={() => setQrType('wechat')}>
                      <div className="w-full aspect-square rounded-xl overflow-hidden border border-gray-100 mb-2">
                        <img src={wechat_qr_url} alt={t('contact_scan_wechat')} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex items-center justify-center gap-1 text-[#07c160] text-xs font-medium">
                        <i className="ri-wechat-line"></i>
                        {t('footer_wechat_label')}
                      </div>
                    </div>
                    <div className="text-center cursor-pointer" onClick={() => setQrType('whatsapp')}>
                      <div className="w-full aspect-square rounded-xl overflow-hidden border border-gray-100 mb-2">
                        <img src={whatsapp_qr_url} alt={t('contact_scan_whatsapp')} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex items-center justify-center gap-1 text-[#25d366] text-xs font-medium">
                        <i className="ri-whatsapp-line"></i>
                        WhatsApp
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Form */}
              <div className="lg:col-span-3 bg-white rounded-2xl p-6 md:p-8">
                <h3 className="font-bold text-[#1a3a5c] text-lg mb-2">{t('contact_form_title')}</h3>
                <p className="text-gray-400 text-sm mb-6">{t('contact_form_desc')}</p>

                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full mb-4">
                      <i className="ri-check-line text-2xl text-green-500"></i>
                    </div>
                    <h3 className="font-bold text-[#1a3a5c] text-base mb-2">{t('appt_success')}</h3>
                    <p className="text-gray-500 text-sm">{t('appt_form_contact_soon')}</p>
                  </div>
                ) : (
                  <form ref={formRef} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">{t('appt_name')} *</label>
                        <input type="text" name="name" required placeholder="请输入姓名" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#c9a96e] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">{t('appt_phone')} *</label>
                        <input type="tel" name="phone" required placeholder="+86 或境外号码" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#c9a96e] transition-colors" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">{t('appt_wechat')}</label>
                        <input type="text" name="wechat" placeholder="微信号" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#c9a96e] transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">{t('appt_email')}</label>
                        <input type="email" name="email" placeholder="email@example.com" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#c9a96e] transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">{t('appt_service')}</label>
                      <select name="service" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#c9a96e] transition-colors cursor-pointer">
                        <option value="">请选择咨询服务</option>
                        <option value="单助孕">{t('appt_service_opt1')}</option>
                        <option value="三代试管">{t('appt_service_opt2')}</option>
                        <option value="试管助孕">{t('appt_service_opt3')}</option>
                        <option value="综合咨询">{t('appt_service_opt4')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#1a3a5c] mb-1.5">{t('appt_message')}</label>
                      <textarea name="message" rows={4} maxLength={500} placeholder="请描述您的具体情况或问题（最多500字）" className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-[#c9a96e] transition-colors resize-none"></textarea>
                    </div>
                    <button type="submit" disabled={loading} className="w-full bg-[#c9a96e] hover:bg-[#b8944f] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm">
                      {loading ? t('loading') : t('appt_submit')}
                    </button>
                    <p className="text-gray-400 text-xs text-center">{t('contact_form_privacy')}</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-xl font-bold text-[#1a3a5c] mb-5">{t('contact_map_title')}</h2>
            <div className="relative w-full h-80 rounded-2xl overflow-hidden bg-[#eae3d8]">
              {/* 最底层占位（兜底图缺失时也不至于留个破图图标） */}
              <div className="absolute inset-0 z-0 flex flex-col items-center justify-center gap-2 text-[#1a3a5c]/45">
                <i className="ri-map-pin-2-line text-3xl"></i>
                <span className="text-sm">{contact.address}</span>
              </div>
              {/* 固定位置截图：默认显示，真地图加载成功后淡出 */}
              <img
                src="/images/contact/contact-map-placeholder.jpg"
                alt={t('contact_map_title_attr')}
                onError={() => setMapImgError(true)}
                className={`absolute inset-0 z-10 w-full h-full object-cover object-top transition-opacity duration-500 ${mapReady || mapImgError ? 'pointer-events-none opacity-0' : 'opacity-100'}`}
              />
              {/* 真实地图：加载成功后淡入盖住截图；国内加载失败则一直不显示，兜底图保持可见 */}
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2924.3782791934!2d74.5698!3d42.8746!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sBishkek%2C+Kyrgyzstan!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t('contact_map_title_attr')}
                onLoad={() => setMapReady(true)}
                className={`relative w-full h-full transition-opacity duration-700 ${mapReady ? 'opacity-100' : 'opacity-0'}`}
              ></iframe>
            </div>
          </div>
        </section>

        {/* Bottom spacing */}
        <div className="h-14 bg-white"></div>

        {/* QR Modal */}
        {qrType && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setQrType(null)}></div>
            <div className="relative bg-white rounded-2xl shadow-xl p-6 w-72 text-center">
              <div className="w-48 h-48 mx-auto mb-4 rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
                <img
                  src={qrType === 'wechat' ? wechat_qr_url : whatsapp_qr_url}
                  alt={qrType === 'wechat' ? t('contact_scan_wechat') : t('contact_scan_whatsapp')}
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="font-semibold text-[#1a3a5c] text-sm mb-1">
                {qrType === 'wechat' ? t('contact_scan_wechat') : t('contact_scan_whatsapp')}
              </p>
              <p className="text-gray-400 text-xs">
                {qrType === 'wechat' ? `${t('contact_wechat')}：${contact.wechat}` : contact.whatsapp}
              </p>
              <button
                onClick={() => setQrType(null)}
                className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-full hover:bg-gray-100"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}
      </main>
    </>
  );
}